import ThemeToggle from "@/components/ui/theme-toggle";
import { useCallCenterSession } from "@/contexts/staff-session/staff-session.context";
import useOnLogout from "@/hooks/use-on-logout.hook";
import wait from "@/utils/wait.utils";
import { ConnectionControl, ConnectionStatus, NetworkStatus, RegistrationStatus } from "@EspaceParkQC/sdk-call-center";
import { Button, cn, Popover, PopoverContent, PopoverTrigger, toast } from "@EspaceParkQC/sdk-react";
import { ChevronDown } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../ui/language-toggle";

export default function Header(): React.ReactElement {
  const logout = useOnLogout();
  const { user } = useCallCenterSession();
  const extension = user?.sipInfo.identity?.split("@")[0].replace("sip:", "");
  const { t } = useTranslation("main");

  return (
    <header className="flex justify-between md:flex-row flex-col items-center md:gap-4 md:p-4 p-0 border-b border-t mt-4">
      <div className="flex items-center md:justify-between justify-start md:gap-4 gap-3 md:w-auto w-full md:p-0 p-4">
        <div className="flex flex-col justify-center flex-1">
          <span className="text-[10px] text-muted-foreground">{t("header.connected-as")}</span>
          <div className="text-sm font-medium">{user?.fullName}</div>
        </div>

        <div className="w-px h-8 bg-border md:block hidden" />

        <ThemeToggle />

        <LanguageToggle />

        <ConnectionControl
          render={({ onConnect, onDisconnect, isConnecting, isDisconnecting, error, isConnected }) => {
            useEffect(() => {
              if (error) {
                toast.error(error.message);
              }
            }, [error]);

            useEffect(() => {
              if (isConnecting) {
                toast.info(t("header.going-online"));
              }
              if (isDisconnecting) {
                toast.info(t("header.going-offline"));
              }
            }, [isConnecting, isDisconnecting, t]);

            useEffect(() => {
              (async () => {
                if (isConnected) {
                  await wait(500); // Give the impression it's loading
                  toast.dismiss();
                  toast.success(t("header.went-online"));
                } else {
                  await wait(500); // Give the impression it's loading
                  toast.dismiss();
                  toast.success(t("header.went-offline"));
                }
              })();
            }, [isConnected]);

            return (
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="sm">
                    {t("header.session")} <ChevronDown className="size-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="space-y-2">
                  <Button size="sm" className="w-full" onClick={logout}>
                    {t("header.logout-from-call-center")}
                  </Button>
                  {!isConnected ? (
                    <Button variant="outline" size="sm" className="w-full" onClick={onConnect} disabled={isConnecting || isDisconnecting}>
                      {t("header.go-online")}
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full" onClick={onDisconnect} disabled={isDisconnecting || isConnecting}>
                      {t("header.go-offline")}
                    </Button>
                  )}
                </PopoverContent>
              </Popover>
            );
          }}
        />
      </div>

      <div className="flex items-center md:justify-between justify-center gap-5 md:w-auto w-full md:border-0 border-t md:p-0 p-4">
        <div className="flex flex-col justify-center">
          <span className="text-[10px] text-muted-foreground">{t("header.extension-number")}</span>
          <div className="text-sm font-medium">{extension}</div>
        </div>

        <div className="w-px h-8 bg-border" />

        <ConnectionStatus
          render={({ isOffline, isConnecting }) => {
            const className = isConnecting ? "text-attention-600" : isOffline ? "text-destructive" : "text-primary";
            return (
              <div className="flex flex-col justify-center">
                <span className="text-[10px] text-muted-foreground">{t("header.server-status")}</span>
                <div className={cn("text-sm font-medium", className)}>
                  {isConnecting ? t("header.connecting-to-sip") : isOffline ? t("header.offline") : t("header.online")}
                </div>
              </div>
            );
          }}
        />

        <div className="w-px h-8 bg-border" />

        <RegistrationStatus
          render={({ isRegistered }) => {
            const className = isRegistered ? "text-primary" : "text-destructive";
            return (
              <div className="flex flex-col justify-center">
                <span className="text-[10px] text-muted-foreground">{t("header.handle-status")}</span>
                <div className={cn("text-sm font-medium", className)}>{isRegistered ? t("header.online") : t("header.offline")}</div>
              </div>
            );
          }}
        />

        <div className="w-px h-8 bg-border" />

        <NetworkStatus
          render={({ isOnline }) => {
            const className = isOnline ? "text-primary" : "text-destructive";
            return (
              <div className="flex flex-col justify-center">
                <span className="text-[10px] text-muted-foreground">{t("header.network-status")}</span>
                <div className={cn("text-sm font-medium", className)}>{isOnline ? t("header.online") : t("header.offline")}</div>
              </div>
            );
          }}
        />
      </div>
    </header>
  );
}
